import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { ArrowLeft } from 'lucide-react';
import { getChangelog, ReleaseNote } from '../../api/release-notes';
import { Loading } from '../../components/ui/Loading';
import { StaggerIn } from '../../components/ui/StaggerIn';

function formatDate(dateString: string): string {
  return new Date(dateString).toLocaleDateString('en-US', {
    year: 'numeric',
    month: 'long',
    day: 'numeric',
  });
}

export function ChangelogPage() {
  const [releases, setReleases] = useState<ReleaseNote[]>([]);
  const [isLoading, setIsLoading] = useState(true);

  useEffect(() => {
    const fetch = async () => {
      try {
        const data = await getChangelog();
        setReleases(data);
      } catch (err) {
        console.error('Failed to fetch changelog:', err);
      } finally {
        setIsLoading(false);
      }
    };
    fetch();
  }, []);

  if (isLoading) {
    return <Loading className="py-20" size="lg" />;
  }

  return (
    <div className="max-w-3xl mx-auto space-y-8">
      <StaggerIn>
        <div className="flex items-center gap-3 mb-2">
          <Link
            to="/"
            className="p-2 -ml-2 rounded-lg text-muted hover:text-text hover:bg-surface-light transition-colors"
          >
            <ArrowLeft className="w-5 h-5" />
          </Link>
          <div>
            <h1 className="text-2xl font-bold text-text">Changelog</h1>
            <p className="text-sm text-muted mt-0.5">What's new in BandMate</p>
          </div>
        </div>
      </StaggerIn>

      {releases.length === 0 ? (
        <p className="text-muted text-center py-12">No release notes yet.</p>
      ) : (
        releases.map((release) => (
          <StaggerIn key={release.id}>
            <div className="relative pl-6 border-l-2 border-border">
              <div className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-primary" />
              <div className="space-y-2">
                <div className="flex items-center gap-2">
                  <span className="text-sm font-semibold text-primary">v{release.version}</span>
                  <span className="text-xs text-muted">{formatDate(release.publishedAt)}</span>
                </div>
                <h3 className="text-lg font-medium text-text">{release.title}</h3>
                {release.highlights && release.highlights.length > 0 && (
                  <ul className="space-y-1 ml-1">
                    {release.highlights.map((h, i) => (
                      <li key={i} className="text-sm text-muted flex gap-2">
                        <span className="text-primary mt-0.5">•</span>
                        <span>{h}</span>
                      </li>
                    ))}
                  </ul>
                )}
              </div>
            </div>
          </StaggerIn>
        ))
      )}
    </div>
  );
}